<template>
  <div class="relative">
    <!-- Загрузка -->
    <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
      <div class="text-center">
        <div class="loading-spinner mx-auto mb-2"></div>
        <p class="text-sm text-gray-500">Загрузка данных...</p>
      </div>
    </div>

    <!-- Ошибка -->
    <div v-else-if="error" class="h-64 flex items-center justify-center">
      <div class="text-center">
        <ExclamationTriangleIcon class="h-8 w-8 mx-auto text-red-400 mb-2" />
        <p class="text-gray-600">Ошибка загрузки диаграммы</p>
        <button @click="loadData" class="mt-2 text-sm text-blue-600 hover:text-blue-800">
          Попробовать снова
        </button>
      </div>
    </div>

    <!-- Пустые данные -->
    <div v-else-if="!chartData || chartData.length === 0" class="h-64 flex items-center justify-center">
      <div class="text-center">
        <ChartBarIcon class="h-8 w-8 mx-auto text-gray-400 mb-2" />
        <p class="text-gray-500">Нет данных для отображения</p>
      </div>
    </div>

    <!-- Диаграмма -->
    <div v-else class="h-64">
      <canvas ref="chartCanvas"></canvas>
    </div>

    <!-- Переключатель периода -->
    <div class="mt-4 flex justify-center">
      <div class="inline-flex rounded-lg border border-gray-200 p-1">
        <button
          v-for="period in periods"
          :key="period.value"
          @click="changePeriod(period.value)"
          :class="[
            'px-3 py-1 text-sm font-medium rounded-md transition-colors',
            selectedPeriod === period.value
              ? 'bg-blue-600 text-white'
              : 'text-gray-700 hover:bg-gray-100'
          ]"
        >
          {{ period.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { ChartBarIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { useChart, createLineChartConfig, chartColors } from '@/composables/useChart'

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  autoLoad: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['periodChange', 'dataLoaded'])

// Состояние
const isLoading = ref(false)
const error = ref(null)
const rawData = ref([])
const chartCanvas = ref(null)
const selectedPeriod = ref(30)

// Периоды для выбора
const periods = [
  { value: 7, label: '7 дней' },
  { value: 14, label: '14 дней' },
  { value: 30, label: '30 дней' }
]

// Фильтрованные данные по выбранному периоду
const chartData = computed(() => {
  if (!rawData.value || rawData.value.length === 0) {
    return []
  }

  // Берем последние N дней из данных
  const filtered = rawData.value.slice(-selectedPeriod.value)
  console.log(`📊 Отфильтровано ${filtered.length} дней из ${rawData.value.length} (период: ${selectedPeriod.value})`)
  return filtered
})

// Конфигурация диаграммы
const chartConfig = computed(() => {
  if (!chartData.value || chartData.value.length === 0) {
    return null
  }

  // Временно только один датасет для диагностики
  const datasets = [
    {
      label: 'Лиды',
      data: chartData.value.map(item => item.leads || 0),
      borderColor: chartColors.primary[0],
      backgroundColor: chartColors.primary[0] + '80',
      borderWidth: 2,
      yAxisID: 'y',
      type: 'bar',
      borderRadius: 4,
      borderSkipped: false
    }
  ]

  return {
    type: 'bar',
    data: {
      labels: chartData.value.map(item => {
        const date = new Date(item.date)
        return date.toLocaleDateString('ru-RU', {
          month: 'short',
          day: 'numeric'
        })
      }),
      datasets
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index'
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            stepSize: 1
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      },
      plugins: {
        legend: {
          position: 'top',
          align: 'end'
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          borderWidth: 1,
          cornerRadius: 8,
          padding: 12,
          callbacks: {
            title: function(context) {
              return context[0].label
            },
            label: function(context) {
              return `${context.dataset.label}: ${context.parsed.y}`
            }
          }
        }
      }
    }
  }
})

// Новый ref для передачи в useChart
const chartConfigRaw = ref(chartConfig.value)

// Watcher для обновления chartConfigRaw при изменении chartConfig
watch(chartConfig, (newConfig) => {
  chartConfigRaw.value = JSON.parse(JSON.stringify(newConfig))
}, { immediate: true, deep: true })

// Используем chartConfigRaw вместо chartConfig
const { createChart, refreshChart } = useChart(chartCanvas, chartConfigRaw)

// Функция для безопасного обновления диаграммы
const updateChartSafely = () => {
  try {
    if (chartConfig.value && chartCanvas.value) {
      refreshChart()
    }
  } catch (err) {
    console.error('❌ Ошибка обновления диаграммы:', err)
  }
}

// Генерация тестовых данных
const generateTestData = (days) => {
  const data = []
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date()
    date.setDate(date.getDate() - i)

    data.push({
      date: date.toISOString().split('T')[0],
      leads: Math.floor(Math.random() * 15) + 5,
      deals: Math.floor(Math.random() * 8) + 2
    })
  }
  return data
}

// Изменение периода
const changePeriod = async (period) => {
  selectedPeriod.value = period
  emit('periodChange', period)
  console.log(`📊 Изменен период на ${period} дней`)

  // Обновляем диаграмму
  await nextTick()
  updateChartSafely()
}

// Загрузка данных
const loadData = async () => {
  if (props.data && props.data.length > 0) {
    // Используем переданные данные
    rawData.value = [...props.data] // Сохраняем в rawData
    emit('dataLoaded', chartData.value)

    // Обновляем диаграмму
    await nextTick()
    updateChartSafely()
    return
  }

  if (!props.autoLoad) return

  try {
    isLoading.value = true
    error.value = null

    console.log(`📊 Загрузка данных активности...`)

    // TODO: Заменить на реальные данные из API
    // Пока используем тестовые данные (генерируем максимум - 30 дней)
    const testData = generateTestData(30)
    rawData.value = testData

    emit('dataLoaded', chartData.value)
    emit('periodChange', selectedPeriod.value)

    console.log('✅ Данные активности загружены:', rawData.value.length)

    // Обновляем диаграмму после загрузки данных
    await nextTick()
    updateChartSafely()

  } catch (err) {
    console.error('❌ Ошибка загрузки данных активности:', err)
    error.value = err.message || 'Ошибка загрузки данных'
  } finally {
    isLoading.value = false
  }
}

// Обновление при изменении props.data (только если действительно изменились)
watch(() => props.data, (newData, oldData) => {
  if (newData && Array.isArray(newData) && newData !== oldData) {
    console.log('📊 ActivityChart: Получены новые данные через props')
    rawData.value = [...newData] // Сохраняем в rawData
    emit('dataLoaded', chartData.value)

    // Безопасное обновление диаграммы
    nextTick(() => {
      updateChartSafely()
    })
  }
}, { immediate: false }) // Убираем immediate

// Инициализация
onMounted(() => {
  loadData()
})

// Экспорт методов для родительского компонента
defineExpose({
  loadData,
  refresh: loadData,
  chartData: chartData,
  changePeriod
})
</script>

<style scoped>
.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #f3f4f6;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>